'use client';
import { useState, useEffect } from 'react';

interface DataPoint {
  date: string;
  total: number;
  texted: number;
  /** null on days with nothing countable — dropped from the line, not plotted as 0. */
  rate: number | null;
}

interface TrendChartProps {
  branch: string;
  leader: string;
}

type Period = '7d' | '30d' | '90d' | '1y';

export default function TrendChart({ branch, leader }: TrendChartProps) {
  const [period, setPeriod] = useState<Period>('7d');
  const [data, setData] = useState<DataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ period });
    if (branch !== 'All') params.set('branch', branch);
    if (leader !== 'All') params.set('leader', leader);
    fetch(`/api/dashboard/history?${params}`, { credentials: 'include' })
      .then(r => r.json())
      // Days with no rate at all (nothing the team could have texted) are
      // dropped rather than drawn as a 0% crash in the line.
      .then(d => setData((d.data || []).filter((p: DataPoint) => p.rate != null)))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [period, branch, leader]);

  const periods: { key: Period; label: string }[] = [
    { key: '7d', label: '7 Days' },
    { key: '30d', label: '30 Days' },
    { key: '90d', label: '90 Days' },
    { key: '1y', label: '1 Year' },
  ];

  // SVG dimensions
  const W = 500, H = 200, PAD = 40, PADR = 20, PADT = 20, PADB = 30;
  const chartW = W - PAD - PADR, chartH = H - PADT - PADB;

  const rates = data.map(d => d.rate as number);
  const minR = Math.min(0, ...rates);
  const maxR = Math.max(100, ...rates);
  const range = maxR - minR || 1;

  const points = data.map((d, i) => ({
    x: PAD + (data.length > 1 ? (i / (data.length - 1)) * chartW : chartW / 2),
    y: PADT + chartH - (((d.rate as number) - minR) / range) * chartH,
  }));

  const polyline = points.map(p => `${p.x},${p.y}`).join(' ');
  const areaPath = points.length > 1
    ? `M${points[0].x},${PADT + chartH} L${polyline} L${points[points.length - 1].x},${PADT + chartH} Z`
    : '';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-semibold text-gray-800">Compliance Trend</h2>
        <div className="flex gap-1">
          {periods.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${period === p.key ? 'bg-[#D86018] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="p-4">
        {loading ? (
          <div className="h-[200px] bg-gray-100 rounded animate-pulse" />
        ) : data.length === 0 ? (
          <div className="h-[200px] flex items-center justify-center text-gray-400">No data available</div>
        ) : (
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
            {/* Grid lines */}
            {[0, 25, 50, 75, 100].map(v => {
              const y = PADT + chartH - ((v - minR) / range) * chartH;
              return (
                <g key={v}>
                  <line x1={PAD} y1={y} x2={W - PADR} y2={y} stroke="#e5e7eb" strokeWidth="0.5" />
                  <text x={PAD - 5} y={y + 3} textAnchor="end" fill="#9ca3af" fontSize="9">{v}%</text>
                </g>
              );
            })}

            {/* Area fill */}
            {areaPath && <path d={areaPath} fill="url(#trendGradient)" opacity="0.3" />}

            {/* Line */}
            {points.length > 1 && <polyline points={polyline} fill="none" stroke="#D86018" strokeWidth="2" strokeLinejoin="round" />}

            {/* Data points */}
            {points.map((p, i) => (
              <g key={i} onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)}>
                <circle cx={p.x} cy={p.y} r={hoveredIndex === i ? 5 : 3} fill="#D86018" stroke="white" strokeWidth="1.5" className="cursor-pointer" />
                {hoveredIndex === i && (
                  <g>
                    <rect x={p.x - 45} y={p.y - 38} width="90" height="28" rx="4" fill="#1f2937" opacity="0.9" />
                    <text x={p.x} y={p.y - 25} textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">{Math.round(data[i].rate as number)}%</text>
                    <text x={p.x} y={p.y - 15} textAnchor="middle" fill="#d1d5db" fontSize="7">{data[i].date} · {data[i].texted}/{data[i].total}</text>
                  </g>
                )}
              </g>
            ))}

            {/* X-axis labels (sparse) */}
            {data.map((d, i) => {
              const step = Math.max(1, Math.floor(data.length / 6));
              if (i % step !== 0 && i !== data.length - 1) return null;
              return (
                <text key={i} x={points[i].x} y={H - 5} textAnchor="middle" fill="#9ca3af" fontSize="8">
                  {d.date.slice(5)}
                </text>
              );
            })}

            <defs>
              <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#D86018" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#D86018" stopOpacity="0.05" />
              </linearGradient>
            </defs>
          </svg>
        )}
      </div>
    </div>
  );
}
